import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { API, authCfg, pad, fmtDMY, CleanerSelect, PdfPrintButtons } from './CleaningScheduleShared';

const TITLES = {
  cleaner: ['Αναλυτική Αναφορά Καθαρίστριας', 'Detailed Cleaner Report'],
  roof: ['Custom Date Range Roof Report', 'Custom Date Range Roof Report'],
  windows: ['Custom Date Range Window Report', 'Custom Date Range Window Report'],
};

const defaultRange = (kind) => {
  const d = new Date();
  const y = d.getFullYear();
  const m = pad(d.getMonth() + 1);
  const last = pad(new Date(y, d.getMonth() + 1, 0).getDate());
  return kind === 'cleaner' ? { from: `${y}-${m}-01`, to: `${y}-${m}-${last}` } : { from: `${y}-01-01`, to: `${y}-12-31` };
};

const ReportTable = ({ items, totals, el, testId }) => (
  <table className="w-full text-xs border border-zinc-200 bg-white" data-testid={testId}>
    <thead><tr className="bg-zinc-900 text-white text-[10px] uppercase tracking-wider">
      {['No.', el ? 'Ημερομηνία' : 'Date', 'Building / Client', el ? 'Καθαρίστρια' : 'Cleaner', el ? 'Εργασία' : 'Job', el ? 'Ώρες' : 'Hours', el ? 'Κόστος' : 'Cost'].map((h, i) => (
        <th key={h} className={`px-2 py-1.5 font-semibold ${i >= 5 ? 'text-right' : 'text-left'}`}>{h}</th>
      ))}
    </tr></thead>
    <tbody>
      {items.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-zinc-400" data-testid={`${testId}-empty`}>{el ? 'Δεν βρέθηκαν εργασίες' : 'No jobs found'}</td></tr>}
      {items.map((it, i) => (
        <tr key={`${it.source}-${it.id}-${it.cleaner_id}-${i}`} className="border-t border-zinc-100 odd:bg-white even:bg-zinc-50/70" data-testid={`${testId}-row`}>
          <td className="px-2 py-1 text-zinc-500 font-mono">{i + 1}</td>
          <td className="px-2 py-1 font-mono">{fmtDMY(it.date)}</td>
          <td className="px-2 py-1 font-medium text-zinc-900">{it.row_name}</td>
          <td className="px-2 py-1">{it.cleaner || '—'}</td>
          <td className="px-2 py-1 text-zinc-600">{it.type}</td>
          <td className="px-2 py-1 text-right font-mono">{it.hours == null ? '' : it.hours.toFixed(2)}</td>
          <td className="px-2 py-1 text-right font-mono text-amber-700">€{it.cost.toFixed(2)}</td>
        </tr>
      ))}
    </tbody>
    <tfoot><tr className="border-t-2 border-zinc-300 bg-zinc-100 font-semibold">
      <td colSpan={5} className="px-2 py-1.5 text-right text-[10px] uppercase" data-testid={`${testId}-total-jobs`}>{el ? 'Σύνολο' : 'Total'}: {totals.jobs} {el ? 'εργασίες' : 'jobs'}</td>
      <td className="px-2 py-1.5 text-right font-mono" data-testid={`${testId}-total-hours`}>{totals.hours.toFixed(2)}</td>
      <td className="px-2 py-1.5 text-right font-mono text-amber-800" data-testid={`${testId}-total-cost`}>€{totals.cost.toFixed(2)}</td>
    </tr></tfoot>
  </table>
);

const Totals = ({ t, el }) => (
  <div className="grid grid-cols-3 border border-zinc-200 bg-white mb-3 divide-x divide-zinc-200" data-testid="report-totals">
    {[[el ? 'Σύνολο Εργασιών' : 'Total Jobs', t.jobs, 'jobs'], [el ? 'Σύνολο Ωρών' : 'Total Hours', t.hours.toFixed(2), 'hours'], [el ? 'Συνολικό Κόστος' : 'Total Cost', `€${t.cost.toFixed(2)}`, 'cost']].map(([l, v, k]) => (
      <div key={k} className="px-3 py-2"><div className="text-[9px] uppercase tracking-wider text-zinc-500 font-semibold">{l}</div>
        <div className="font-mono font-bold text-base" data-testid={`report-total-${k}`}>{v}</div></div>
    ))}
  </div>
);

export const CleaningReports = ({ kind, el, cleaners, rowOptions }) => {
  const [draft, setDraft] = useState({ ...defaultRange(kind), cleaner_id: '', row_key: '' });
  const [applied, setApplied] = useState(draft);
  const [data, setData] = useState(null);

  const load = useCallback(async (f) => {
    try {
      const q = new URLSearchParams({ kind, date_from: f.from, date_to: f.to, cleaner_id: f.cleaner_id, row_key: f.row_key });
      setData((await axios.get(`${API}/cleaning/reports?${q}`, authCfg())).data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  }, [kind]);
  useEffect(() => { load(applied); }, [applied, load]);

  const apply = () => { if (!draft.from || !draft.to || draft.from > draft.to) { toast.error(el ? 'Μη έγκυρη περίοδος' : 'Invalid date range'); return; } setApplied({ ...draft }); };
  const clear = () => { const d = { ...defaultRange(kind), cleaner_id: '', row_key: '' }; setDraft(d); setApplied(d); };
  const set = (k) => (v) => setDraft({ ...draft, [k]: v });
  const cl = cleaners.find((c) => c.cleaner_id === applied.cleaner_id);
  const title = `${TITLES[kind][el ? 0 : 1]}${cl ? ` — ${cl.name}` : ''}`;
  return (
    <div data-testid={`report-${kind}`}>
      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid={`report-${kind}-filters`}>
        <span className="text-[11px] text-zinc-500">From Date</span>
        <Input type="date" value={draft.from} onChange={(e) => set('from')(e.target.value)} className="h-8 w-36 rounded-none text-xs" data-testid="report-date-from" />
        <span className="text-[11px] text-zinc-500">To Date</span>
        <Input type="date" value={draft.to} onChange={(e) => set('to')(e.target.value)} className="h-8 w-36 rounded-none text-xs" data-testid="report-date-to" />
        <CleanerSelect cleaners={cleaners} value={draft.cleaner_id} onChange={set('cleaner_id')} el={el} testId="report-cleaner-select" />
        <select value={draft.row_key} onChange={(e) => set('row_key')(e.target.value)} className="h-8 border border-zinc-300 bg-white px-2 text-xs max-w-[220px]" data-testid="report-client-select">
          <option value="">{el ? 'Όλα τα κτίρια / πελάτες' : 'All Buildings / Clients'}</option>
          {rowOptions.map((r) => <option key={r.row_key} value={r.row_key}>{r.name}</option>)}
        </select>
        <Button size="sm" className="rounded-none h-8 bg-zinc-950 text-xs" onClick={apply} data-testid="report-apply-filter">Apply Filter</Button>
        <Button size="sm" variant="outline" className="rounded-none h-8 text-xs" onClick={clear} data-testid="report-clear-filter">Clear Filter</Button>
        <div className="ml-auto">
          <PdfPrintButtons label={TITLES[kind][1]} path="/cleaning/reports/pdf" testId={`report-${kind}-export`}
            params={{ kind, date_from: applied.from, date_to: applied.to, cleaner_id: applied.cleaner_id, row_key: applied.row_key, lang: el ? 'el' : 'en' }} />
        </div>
      </div>
      <div className="mb-2">
        <h3 className="text-sm font-bold text-zinc-900" data-testid="report-title">{title}</h3>
        <p className="text-[11px] text-zinc-500" data-testid="report-period">{el ? 'Περίοδος' : 'Period'}: {fmtDMY(applied.from)} – {fmtDMY(applied.to)}</p>
      </div>
      {!data ? <div className="py-8 text-center text-zinc-400">…</div> : (
        <>
          <Totals t={data.totals} el={el} />
          {kind === 'cleaner' && !applied.cleaner_id && data.groups.length > 0 ? data.groups.map((g) => (
            <div key={g.cleaner_id} className="mb-4" data-testid={`report-group-${g.cleaner_id}`}>
              <div className="text-xs font-bold text-zinc-800 mb-1">{g.cleaner || '—'} <span className="font-mono font-normal text-zinc-500">· {g.totals.jobs} · {g.totals.hours.toFixed(2)}h · €{g.totals.cost.toFixed(2)}</span></div>
              <ReportTable items={g.items} totals={g.totals} el={el} testId={`report-table-${g.cleaner_id}`} />
            </div>
          )) : <ReportTable items={data.items} totals={data.totals} el={el} testId="report-table" />}
        </>
      )}
    </div>
  );
};
